import { ApplicationStage } from 'common';

export enum OfferPageScreen {
  FullOffer = 'full-offer',
  Processing = 'processing',
  OfferExpired = 'offer-expired',
  LookingForFinancing = 'looking-for-financing',
  UnableToFund = 'unable-to-fund'
}

// Exhaustive by construction: Record<ApplicationStage, …> is a compile error when a member is
// missing, so a stage added to the enum cannot silently inherit a fallback.
//
// This table deliberately does NOT mirror ApplicationStage.OfferSelectionAllowed. That attribute is
// true for exactly five stages — ConditionalOffer 700, Approved 900, DirectProposal 950,
// Accepted 1000, Closing 1010 — and gates offer *mutation* in UpdateOfferBundleCommandHandler:69,
// not what a customer may be shown. Two divergences are intentional:
//   - ConditionalOffer is hidden even though acceptance is permitted there. Product's call
//     (RYNO-14826, Carlos Jaramillo, comment 82079: "Conditional Offer DO NOT counts as Approved");
//     the approved Figma frame covers Processing and Conditional Offer together. This closes a live
//     conversion path, deliberately.
//   - UnderwritingCall 1040, SalesReview 1050, ClosingIncomplete 1020 and ClosingReview 1030 still
//     show the full offer even though acceptance is refused there, as do the post-funding stages.
//     Unchanged from master; this ticket did not set out to change them.

export const SCREEN_BY_STAGE: Record<ApplicationStage, OfferPageScreen> = {
  [ApplicationStage.New]: OfferPageScreen.Processing,
  [ApplicationStage.ApplicationCreated]: OfferPageScreen.Processing,
  [ApplicationStage.ApplicationIncomplete]: OfferPageScreen.Processing,
  [ApplicationStage.MODALProcessing]: OfferPageScreen.Processing,
  [ApplicationStage.MODALError]: OfferPageScreen.Processing,
  // Pre-decision review, not a decline — the customer-facing mapping sends it to Processing too.
  [ApplicationStage.DeclinedReview]: OfferPageScreen.Processing,
  [ApplicationStage.PreUnderwriting]: OfferPageScreen.Processing,
  [ApplicationStage.Underwriting]: OfferPageScreen.Processing,
  [ApplicationStage.UnderwritingLitigation]: OfferPageScreen.Processing,
  [ApplicationStage.UnderwritingMissingInfo]: OfferPageScreen.Processing,
  [ApplicationStage.UnderwritingReview]: OfferPageScreen.Processing,
  [ApplicationStage.ConditionalOffer]: OfferPageScreen.Processing,
  [ApplicationStage.ConditionalApprovalNAICSCheck]: OfferPageScreen.Processing,

  [ApplicationStage.Declined]: OfferPageScreen.UnableToFund,

  [ApplicationStage.Nurture]: OfferPageScreen.OfferExpired,
  [ApplicationStage.Expired]: OfferPageScreen.OfferExpired,
  [ApplicationStage.Dormant]: OfferPageScreen.OfferExpired,

  [ApplicationStage.NotInterested]: OfferPageScreen.LookingForFinancing,

  [ApplicationStage.Approved]: OfferPageScreen.FullOffer,
  [ApplicationStage.DirectProposal]: OfferPageScreen.FullOffer,
  [ApplicationStage.Accepted]: OfferPageScreen.FullOffer,
  [ApplicationStage.Closing]: OfferPageScreen.FullOffer,
  [ApplicationStage.ClosingIncomplete]: OfferPageScreen.FullOffer,
  [ApplicationStage.ClosingReview]: OfferPageScreen.FullOffer,
  [ApplicationStage.UnderwritingCall]: OfferPageScreen.FullOffer,
  [ApplicationStage.SalesReview]: OfferPageScreen.FullOffer,
  [ApplicationStage.PendingSignature]: OfferPageScreen.FullOffer,
  [ApplicationStage.Open]: OfferPageScreen.FullOffer,
  [ApplicationStage.Frozen]: OfferPageScreen.FullOffer,
  // Not classifiable from stage alone (funded vs never-funded); today's behaviour is the full offer.
  [ApplicationStage.Closed]: OfferPageScreen.FullOffer,
  [ApplicationStage.PendingRenewal]: OfferPageScreen.FullOffer,
  [ApplicationStage.Renewed]: OfferPageScreen.FullOffer
};

// Salesforce owns the stage value, so an unknown string can arrive. Processing is the fail-safe:
// it never reveals an offer.
export function offerPageScreenFor(stage: ApplicationStage): OfferPageScreen {
  return SCREEN_BY_STAGE[stage] ?? OfferPageScreen.Processing;
}
